// React and third-party libraries
import { getZaniaSocket } from "@/infra/sockets";
import useSocket from "@/shared/hooks/use-socket";
import { yupResolver } from "@hookform/resolvers/yup";
import { Slider, Spinner, Switch } from "@nextui-org/react";
import clsx from "clsx";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useBlocker, useNavigate, useParams } from "react-router-dom";

// Agent module imports
import InlineSources from "@/modules/agent/components/review-responses/InlineSources";
import { ReviewSourceTypes } from "@/modules/agent/types";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";

// Shared components and utilities
import { ZButton } from "@/shared/components/button";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { UploadedFile } from "@/shared/types/file-upload";
import { cn } from "@/shared/utils/classname-merger";
import parsePromise from "@/shared/utils/parsePromise";

// Module-specific imports
import { questionnaireFormSchema } from "../../constants/form-validation";
import { getQuestionnaireDetails, QuestionnaireDetailsResponse } from "../../requests";
import {
  AddEvidencePayload,
  addQuestionnaireEvidence,
  createQuestionnaire,
  deleteQuestionnaireEvidence,
  Evidence,
  extractQuestionsFromDocument,
  getQuestionnaireEvidences,
  Questionnaire,
  refineValidationRules,
  SaveQuestionPayload,
  saveQuestions,
  UpdateEvidencePayload,
  updateQuestionnaireEvidence
} from "../../requests/questionnaire";

// Local component imports
import FileUpload from "@/shared/components/custom/file-upload";
import ExitModal from "@/shared/components/custom/modals/ExitModal";
import QuestionList from "./QuestionList";
import { deduplicateResponseChoices, findDuplicateOptionIndices } from "./questionTreeUtils";

export type TabType = 'documents' | 'questions';

// ValidationRulesComponent for numbered list with auto-creation
interface ValidationRulesComponentProps {
  defaultValue: {
    value: string[];
    sources: ReviewSourceTypes[];
    isEdited?: boolean;
  };
  onGetValues: () => string[]; // Callback to get current values when needed (e.g., on save)
  onSourceClick: () => void;
  setListOfSourcesForSelectedSources: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  onRefineClick?: () => void;
  isRefining?: boolean;
  onEdit?: () => void;
  validationRulesRef?: React.MutableRefObject<{ getCurrentValues: () => string[] } | null>;
}

const ValidationRulesComponent = ({
  defaultValue,
  onGetValues,
  onSourceClick,
  setListOfSourcesForSelectedSources,
  placeholder,
  isDisabled,
  onRefineClick,
  isRefining,
  onEdit,
  validationRulesRef
}: ValidationRulesComponentProps) => {
  const [localValues, setLocalValues] = useState<string[]>([]);
  const isInitializedRef = useRef(false);
  const needsResizeRef = useRef(true); // Track if we need to resize on next render

  // Calculate if Refine with AI should be disabled
  const hasValidationRules = localValues.filter(v => v.trim() !== '').length > 0;
  const isRefineDisabled = !hasValidationRules || isDisabled;
  const isRefineClickDisabled = isRefineDisabled || isRefining;

  // Initialize local values ONLY ONCE on mount or when defaultValue changes externally
  useEffect(() => {
    const initialValues = [...defaultValue.value];
    // Always ensure there's an empty field at the end for new input
    if (initialValues.length === 0 || initialValues[initialValues.length - 1] !== "") {
      initialValues.push("");
    }
    setLocalValues(initialValues);
    isInitializedRef.current = true;
    needsResizeRef.current = true; // Mark that we need to resize after this update
  }, [defaultValue.value.join('|')]); // Only re-initialize when the actual values change (deep comparison)

  // Auto-resize textareas when needed or when textareas become visible
  useEffect(() => {
    if (needsResizeRef.current && localValues.length > 0) {
      // Use double requestAnimationFrame to ensure DOM is fully ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const textareas = document.querySelectorAll('.validation-rule-textarea');
          if (textareas.length > 0) {
            textareas.forEach((textarea: any) => {
              if (textarea && textarea.value) {
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
              }
            });
            needsResizeRef.current = false; // Reset flag after successful resize
          }
        });
      });
    }
  }, [localValues]);

  // Additional resize when component becomes visible with existing data
  useEffect(() => {
    if (localValues.length > 0 && localValues.some(v => v.includes('\n'))) {
      // Check if any value has newlines, then ensure resize happens
      const timer = setTimeout(() => {
        const textareas = document.querySelectorAll('.validation-rule-textarea');
        textareas.forEach((textarea: any) => {
          if (textarea && textarea.value && textarea.scrollHeight > textarea.clientHeight + 5) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
          }
        });
      }, 50); // Small delay for when entering edit mode
      return () => clearTimeout(timer);
    }
  }, []); // Run once on mount

  // Expose getCurrentValues method via ref
  useEffect(() => {
    if (validationRulesRef) {
      validationRulesRef.current = {
        getCurrentValues: () => localValues.filter(v => v.trim() !== '')
      };
    }
  }, [localValues, validationRulesRef]);

  const handleFieldChange = useCallback(
    (newValue: string, index: number) => {
      // Call onEdit to discard refine popup when user starts editing
      if (onEdit) {
        onEdit();
      }

      setLocalValues(currentValues => {
        const newArray = [...currentValues];
        const isEffectivelyEmpty = newValue.trim() === '';

        if (isEffectivelyEmpty) {
          // If the value is empty, remove this field only if it's not the last field
          if (index !== newArray.length - 1) {
            newArray.splice(index, 1);
          } else {
            // If it's the last field, just keep it as empty
            newArray[index] = '';
          }
        } else {
          // Update the field value
          newArray[index] = newValue;

          // If this is the last field and now has content, add a new empty field
          if (index === newArray.length - 1 && newValue.trim() !== '') {
            newArray.push('');
          }
        }

        // NO MORE onChange call here - just update local state
        return newArray;
      });
    },
    [onEdit]
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '424px' }}>
      {/* Frame 6305 - Header Section */}
      <div
        style={{
          /* Frame 6303 */
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          padding: '0px',
          gap: '8px',
          width: '424px',
          height: '20px',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0,
          marginTop: '8px'
        }}
      >
        {/* Validation Rules */}
        <span
          style={{
            width: '223px',
            height: '20px',
            fontFamily: 'Inter',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
            color: '#3F3F46',
            flex: 'none',
            order: 0,
            flexGrow: 1
          }}
        >
          Validation Rules
        </span>

        {/* Frame 4416 - Refine with AI */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px',
            gap: '8px',
            width: '193px',
            height: '20px',
            flex: 'none',
            order: 1,
            flexGrow: 0,
            cursor: isRefineClickDisabled ? 'not-allowed' : 'pointer',
            opacity: isRefineDisabled ? 0.5 : 1
          }}
          onClick={() => {
            if (onRefineClick && !isRefining && hasValidationRules && !isDisabled) {
              onRefineClick();
            }
          }}
        >
          {/* Show spinner when refining, otherwise show icon and text */}
          {isRefining ? (
            <Spinner size="sm" />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '6px',
                width: '193px',
                height: '20px',
                flex: 'none',
                order: 0,
                flexGrow: 0
              }}
            >
              {/* Sparkles Icon */}
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                style={{
                  width: '12px',
                  height: '12px',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0
                }}
              >
                <path
                  d="M6 0.5L7.5 4.5L11.5 6L7.5 7.5L6 11.5L4.5 7.5L0.5 6L4.5 4.5L6 0.5Z"
                  stroke={isRefineDisabled ? "#A1A1AA" : "#006FEE"}
                  strokeWidth="1"
                  fill="none"
                />
              </svg>

              {/* Refine with AI (recommended) */}
              <span
                style={{
                  width: '175px',
                  height: '20px',
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: isRefineDisabled ? '#A1A1AA' : '#006FEE',
                  flex: 'none',
                  order: 1,
                  flexGrow: 0
                }}
              >
                Refine with AI (recommended)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Frame 4232 - Main Validation Rules Container */}
      <div
        style={{
          /* Frame 4232 */
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          width: '424px',
          height: '120px',
          background: '#FAFAFA',
          borderRadius: '4px',
          flex: 'none',
          order: 1,
          alignSelf: 'stretch',
          flexGrow: 0,
          overflow: 'hidden',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        {/* Custom styled validation rules list */}
        <div
          className="validation-rules-scroll-container"
          style={{
            width: '424px',
            height: '120px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '8px',
            boxSizing: 'border-box'
          }}
        >
          <style>{`
          .validation-rules-scroll-container {
            width: 424px !important;
            max-width: 424px !important;
          }
          
          /* Custom scrollbar styling */
          .validation-rules-scroll-container::-webkit-scrollbar {
            width: 6px;
          }
          
          .validation-rules-scroll-container::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .validation-rules-scroll-container::-webkit-scrollbar-thumb {
            background: #D1D5DB;
            border-radius: 3px;
          }
          
          .validation-rules-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #9CA3AF;
          }
        `}</style>

          {/* Custom validation rules list matching Figma exactly */}
          {localValues.map((fieldValue, index) => {
            const isLastField = index === localValues.length - 1;
            const isEmpty = fieldValue.trim() === "";

            // Don't render the last empty field if component is disabled
            if (isDisabled && isLastField && isEmpty) {
              return null;
            }

            return (
              <div
                key={index}
                style={{
                  /* Response Fields */
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: '8px 8px 4px 8px',
                  gap: '4px',
                  width: '100%',
                  minHeight: '36px',
                  flex: 'none',
                  order: index,
                  alignSelf: 'stretch',
                  flexGrow: 0,
                  boxSizing: 'border-box',
                  overflow: 'visible'
                }}
              >
                {/* Number */}
                <span
                  style={{
                    width: '18px',
                    flexShrink: 0,
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#333333',
                    paddingTop: '0px'
                  }}
                >
                  {index + 1}.
                </span>

                {/* Text Field */}
                <textarea
                  className="validation-rule-textarea"
                  value={fieldValue}
                  onInput={(e) => {
                    // Auto-resize happens BEFORE React state update, preserving scroll
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                  onChange={(e) => {
                    handleFieldChange(e.target.value, index);
                  }}
                  placeholder={isLastField ? (placeholder || "Add Rule") : ""}
                  disabled={isDisabled}
                  style={{
                    /* Text content */
                    width: 'calc(100% - 22px)',
                    minHeight: '20px',
                    height: 'auto',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: isEmpty && isLastField ? '#717171' : '#333333',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    padding: '0px',
                    margin: '0px',
                    flex: 1,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    opacity: isEmpty && isLastField ? 0.5 : 1,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflow: 'hidden'
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Sources section if needed - for now empty but ready for future enhancement */}
        {defaultValue.sources && defaultValue.sources.length > 0 && (
          <div className="px-4 pt-4">
            <InlineSources
              sources={defaultValue.sources}
              onSourceClick={onSourceClick}
              setListOfSourcesForSelectedSources={setListOfSourcesForSelectedSources}
              tooltipPlacement="bottom"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// DocumentsTab Component
interface DocumentsTabProps {
  documentsState: DocumentsState;
  setDocumentsState: (state: DocumentsState | ((prev: DocumentsState) => DocumentsState)) => void;
  isCreating: boolean;
  currentQuestionnaireId?: string;
}

const DocumentsTab = ({ documentsState, setDocumentsState, isCreating, currentQuestionnaireId }: DocumentsTabProps) => {
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);

  const handleAddDocument = useCallback(() => {
    // First, clean up any incomplete documents that are not in edit mode
    // (these shouldn't exist, but clean them up if they do)
    const cleanedDocuments = documentsState.documents.filter(doc => {
      const isIncomplete = !doc.name.trim();
      return !isIncomplete || doc.isEditing; // Keep complete docs OR docs being edited
    });

    // Update state with cleaned documents if any were removed
    if (cleanedDocuments.length !== documentsState.documents.length) {
      setDocumentsState(prev => ({
        ...prev,
        documents: cleanedDocuments
      }));
    }

    // Check if any document is currently being edited
    const hasEditingDocument = cleanedDocuments.some(doc => doc.isEditing);
    if (hasEditingDocument) {
      addNotification({
        type: 'warn',
        message: 'Please save or close the current document before adding a new one.',
      });
      return;
    }

    const newDocument: Document = {
      id: Date.now().toString(),
      name: '',
      isOptional: false, // Default to false (required = true)
      fileCount: 1, // Ensure > 0 by default
      validationRules: {
        value: [],
        sources: [],
        isEdited: false
      },
      isEditing: true
    };

    setDocumentsState(prev => ({
      ...prev,
      documents: [...prev.documents, newDocument]
    }));
  }, [documentsState.documents, setDocumentsState]);

  const handleEditDocument = useCallback((documentId: string) => {
    setDocumentsState(prev => ({
      ...prev,
      documents: prev.documents.map(doc =>
        doc.id === documentId ? { ...doc, isEditing: true } : { ...doc, isEditing: false }
      )
    }));
  }, [setDocumentsState]);

  const handleDeleteDocument = useCallback(async (documentId: string) => {
    // Prevent multiple concurrent delete requests
    if (deletingDocumentId) return;

    const docToDelete = documentsState.documents.find(d => d.id === documentId);

    if (!docToDelete) return;

    // Set the deleting state to prevent concurrent deletes
    setDeletingDocumentId(documentId);

    // If in edit mode and document is from API (existing evidence), call DELETE API
    if (currentQuestionnaireId) {
      const isExistingEvidence = isNaN(Number(documentId));

      if (isExistingEvidence) {
        try {
          const [error, response] = await parsePromise(
            deleteQuestionnaireEvidence(currentQuestionnaireId, documentId)
          );

          if (error) {
            console.error('Failed to delete evidence:', error);
            addNotification({
              type: 'error',
              message: 'Failed to delete document. Please try again.',
            });
            setDeletingDocumentId(null);
            return;
          }

          if (response?.success) {
            addNotification({
              type: 'success',
              message: `Document "${docToDelete.name}" deleted successfully`,
            });

            // Remove from local state
            setDocumentsState(prev => ({
              ...prev,
              documents: prev.documents.filter(doc => doc.id !== documentId),
              totalFilesUsed: prev.totalFilesUsed - docToDelete.fileCount
            }));
          }
        } catch (error) {
          console.error('Error deleting evidence:', error);
          addNotification({
            type: 'error',
            message: 'Failed to delete document. Please try again.',
          });
        } finally {
          setDeletingDocumentId(null);
        }
        return;
      }
    }

    // For new documents (not yet saved to API) or create mode, just remove from state
    setDocumentsState(prev => ({
      ...prev,
      documents: prev.documents.filter(doc => doc.id !== documentId),
      totalFilesUsed: prev.totalFilesUsed - docToDelete.fileCount
    }));
    setDeletingDocumentId(null);
  }, [documentsState.documents, currentQuestionnaireId, setDocumentsState, deletingDocumentId]);

  // Calculate total files including documents in editing mode
  const totalFilesIncludingEditing = documentsState.documents.reduce((sum, doc) => sum + doc.fileCount, 0);
  const canAddMore = totalFilesIncludingEditing < 50;

  return (
    <div className="flex flex-col h-full">
      {/* Header matching Questions tab pattern */}
      <div className="flex flex-row justify-between items-center mb-6">
        <h3 className="text-sm font-semibold text-[#18181B]">Request Documents</h3>
        <ZButton
          variant="bordered"
          className="text-xs font-medium h-7 px-3"
          onClick={handleAddDocument}
          isDisabled={isCreating || !canAddMore}
        >
          Add Document
        </ZButton>
      </div>

      {/* Documents grid - only existing documents */}
      <div className="grow overflow-auto">
        <div className="flex justify-center w-full">
          {/* Frame 6335 - Main Container */}
          <div className="flex flex-col items-start w-full max-w-[960px]">
            {/* Frame 6336 - Documents Container */}
            <div className="w-full max-w-[928px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Existing Documents */}
                {documentsState.documents.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    documentsState={documentsState}
                    setDocumentsState={setDocumentsState}
                    isCreating={isCreating}
                    currentQuestionnaireId={currentQuestionnaireId}
                    onEdit={() => handleEditDocument(doc.id)}
                    onDelete={() => handleDeleteDocument(doc.id)}
                    isDeletingDocument={deletingDocumentId === doc.id}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// DocumentCard Component
interface DocumentCardProps {
  document: Document;
  documentsState: DocumentsState;
  setDocumentsState: (state: DocumentsState | ((prev: DocumentsState) => DocumentsState)) => void;
  isCreating: boolean;
  currentQuestionnaireId?: string;
  onEdit: () => void;
  onDelete: () => void;
  isDeletingDocument?: boolean;
}

const DocumentCard = ({ document, documentsState, setDocumentsState, isCreating, currentQuestionnaireId, onEdit, onDelete, isDeletingDocument = false }: DocumentCardProps) => {
  const [formData, setFormData] = useState<Document>(document);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [refinePopover, setRefinePopover] = useState<{
    isVisible: boolean;
    originalRules: string[];
    refinedRules: string[];
  }>({
    isVisible: false,
    originalRules: [],
    refinedRules: [],
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const validationRulesRef = useRef<{ getCurrentValues: () => string[] } | null>(null);

  // Update form data when document changes (e.g., when switching between edit modes)
  useEffect(() => {
    if (!document.isEditing) {
      setFormData(document);
    }
  }, [document]);

  // Auto-discard refine popup when document exits edit mode (e.g., switching to another document)
  useEffect(() => {
    if (!document.isEditing && refinePopover.isVisible) {
      setRefinePopover({
        isVisible: false,
        originalRules: [],
        refinedRules: [],
      });
    }
  }, [document.isEditing, refinePopover.isVisible]);

  // Validation function
  const isDocumentValid = useCallback(() => {
    return formData.name.trim() !== '' &&
      formData.fileCount > 0;
  }, [formData]);

  const handleSave = useCallback(async () => {
    // Enhanced validation
    if (!formData.name.trim()) {
      addNotification({
        type: 'error',
        message: 'Document name is required',
      });
      return;
    }

    if (formData.fileCount <= 0) {
      addNotification({
        type: 'error',
        message: 'Number of files must be greater than 0',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Get current validation rules from the component
      const currentValidationRules = validationRulesRef.current?.getCurrentValues() || [];

      // If we have currentQuestionnaireId (edit mode), call the API
      if (currentQuestionnaireId) {
        // Check if this is an existing evidence (from API) or a new one (created locally)
        // Existing evidence IDs are UUIDs (hex strings), new ones are timestamps (numbers)
        const isExistingEvidence = isNaN(Number(document.id));

        if (isExistingEvidence) {
          // Update existing evidence using PATCH
          const payload: UpdateEvidencePayload = {
            name: formData.name,
            max_files: formData.fileCount,
            rules: currentValidationRules,
            required: !formData.isOptional
          };

          const [error, response] = await parsePromise(
            updateQuestionnaireEvidence(currentQuestionnaireId, document.id, payload)
          );

          if (error) {
            console.error('Failed to update evidence:', error);
            const errorType = (error as any)?.response?.data?.error_type;
            const isValidationError = errorType === 'Validation Error';

            addNotification({
              type: 'error',
              message: isValidationError
                ? 'A document with this name already exists. Please choose a different name.'
                : 'Failed to update document. Please try again.',
            });
            return;
          }

          if (response?.success && response.evidence) {
            // Transform the API response back to Document format
            const updatedDocument: Document = {
              id: response.evidence.id,
              name: response.evidence.document_name,
              isOptional: !response.evidence.required,
              fileCount: response.evidence.max_number_of_files,
              validationRules: {
                value: response.evidence.rules,
                sources: [],
                isEdited: false
              }
            };

            // Update the documents state with API response
            setDocumentsState(prev => {
              const oldDocument = prev.documents.find(d => d.id === document.id);
              const newTotalFiles = prev.totalFilesUsed - (oldDocument?.fileCount || 0) + updatedDocument.fileCount;

              return {
                ...prev,
                documents: prev.documents.map(doc =>
                  doc.id === document.id ? { ...updatedDocument, isEditing: false } : doc
                ),
                totalFilesUsed: newTotalFiles
              };
            });

            addNotification({
              type: 'success',
              message: `Document "${updatedDocument.name}" updated successfully`,
            });
          }
        } else {
          // Add new evidence using POST
          const payload: AddEvidencePayload = {
            name: formData.name,
            required: !formData.isOptional,
            max_files: formData.fileCount,
            rules: currentValidationRules
          };

          const [error, response] = await parsePromise(
            addQuestionnaireEvidence(currentQuestionnaireId, payload)
          );

          if (error) {
            console.error('Failed to add evidence:', error);
            const errorType = (error as any)?.response?.data?.error_type;
            const isValidationError = errorType === 'Validation Error';

            addNotification({
              type: 'error',
              message: isValidationError
                ? 'A document with this name already exists. Please choose a different name.'
                : 'Failed to add document. Please try again.',
            });
            return;
          }

          if (response?.success && response.evidence) {
            // Transform the API response back to Document format
            const newDocument: Document = {
              id: response.evidence.id,
              name: response.evidence.document_name,
              isOptional: !response.evidence.required,
              fileCount: response.evidence.max_number_of_files,
              validationRules: {
                value: response.evidence.rules,
                sources: [],
                isEdited: false
              }
            };

            // Update the documents state with API response
            setDocumentsState(prev => {
              const oldDocument = prev.documents.find(d => d.id === formData.id);
              const newTotalFiles = prev.totalFilesUsed - (oldDocument?.fileCount || 0) + newDocument.fileCount;

              return {
                ...prev,
                documents: prev.documents.map(doc =>
                  doc.id === formData.id ? { ...newDocument, isEditing: false } : doc
                ),
                totalFilesUsed: newTotalFiles
              };
            });

            addNotification({
              type: 'success',
              message: `Document "${newDocument.name}" added successfully`,
            });
          }
        }
      } else {
        // Create mode: just update local state (no API call)
        const updatedFormData = {
          ...formData,
          validationRules: {
            value: currentValidationRules,
            sources: formData.validationRules.sources,
            isEdited: true
          }
        };

        setDocumentsState(prev => {
          const isNewDocument = !prev.documents.find(d => d.id === formData.id && d.name.trim());
          let newTotalFiles = prev.totalFilesUsed;

          if (isNewDocument) {
            newTotalFiles += formData.fileCount;
          } else {
            const oldDocument = prev.documents.find(d => d.id === formData.id);
            newTotalFiles = newTotalFiles - (oldDocument?.fileCount || 0) + formData.fileCount;
          }

          return {
            ...prev,
            documents: prev.documents.map(doc =>
              doc.id === formData.id
                ? { ...updatedFormData, isEditing: false }
                : doc
            ),
            totalFilesUsed: newTotalFiles
          };
        });

        addNotification({
          type: 'success',
          message: `Document "${formData.name}" ${!document.name ? 'added' : 'updated'} successfully`,
        });
      }
    } catch (error) {
      console.error('Error saving document:', error);
      addNotification({
        type: 'error',
        message: 'Failed to save document. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [formData, document.name, setDocumentsState, currentQuestionnaireId]);

  // Handle Refine with AI
  const handleRefineValidationRules = useCallback(async () => {
    // Get current validation rules from the component
    const currentRules = validationRulesRef.current?.getCurrentValues() || [];

    // Abort any existing refine request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsRefining(true);

    try {
      const [error, response] = await parsePromise(
        refineValidationRules({ rules: currentRules })
      );

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (error) {
        console.error('Failed to refine validation rules:', error);
        addNotification({
          type: 'error',
          message: 'Failed to refine validation rules. Please try again.',
        });
        return;
      }

      if (response?.success && response.rules) {
        setRefinePopover({
          isVisible: true,
          originalRules: currentRules,
          refinedRules: response.rules,
        });
      }
    } catch (error) {
      // Ignore abort errors
      if ((error as any)?.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }

      console.error('Error refining validation rules:', error);
      addNotification({
        type: 'error',
        message: 'Failed to refine validation rules. Please try again.',
      });
    } finally {
      if (!abortController.signal.aborted) {
        setIsRefining(false);
      }
      // Clear the ref if this is the current controller
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  // Handle Accept Refined Rules
  const handleAcceptRefinedRules = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      validationRules: {
        ...prev.validationRules,
        value: refinePopover.refinedRules,
      }
    }));
    setRefinePopover({
      isVisible: false,
      originalRules: [],
      refinedRules: [],
    });
  }, [refinePopover.refinedRules]);

  // Handle Discard Refined Rules
  const handleDiscardRefinedRules = useCallback(() => {
    // Abort any ongoing refine API call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsRefining(false);
    }

    setRefinePopover({
      isVisible: false,
      originalRules: [],
      refinedRules: [],
    });
  }, []);

  // Calculate files used by OTHER evidences (excluding current document)
  const filesUsedByOthers = documentsState.documents
    .filter(doc => doc.id !== document.id)
    .reduce((sum, doc) => sum + doc.fileCount, 0);
  // Maximum this evidence can have (absolute cap)
  const maxSliderValue = 50 - filesUsedByOthers;

  // Edit mode
  if (document.isEditing) {
    return (
      <>
        <div
          ref={cardRef}
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '16px',
            gap: '16px',
            width: '456px',
            height: '314px',
            background: '#FFFFFF',
            border: '1px solid #A1A1AA',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
            borderRadius: '8px',
            flex: 'none',
            order: 0,
            flexGrow: 1,
            zIndex: 0,
            position: 'relative'
          }}
        >
          {/* Frame 6304 - Top Section */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '0px',
              gap: '12px',
              width: '424px',
              height: '112px',
              flex: 'none',
              order: 0,
              alignSelf: 'stretch',
              flexGrow: 0
            }}
          >
            {/* Frame 6297 - Document Name Row */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '6px',
                width: '424px',
                height: '24px',
                flex: 'none',
                order: 0,
                alignSelf: 'stretch',
                flexGrow: 0
              }}
            >
              {/* Add Document Name Input */}
              <div
                style={{
                  width: '362px',
                  height: '20px',
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: formData.name ? '#18181B' : '#A1A1AA',
                  flex: 'none',
                  order: 1,
                  flexGrow: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <input
                  type="text"
                  placeholder="Add Document Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={isCreating}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: formData.name ? '#18181B' : '#A1A1AA'
                  }}
                />
              </div>

              {/* Frame 6298 - Action Buttons */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: '0px',
                  gap: '8px',
                  width: '56px',
                  height: '24px',
                  flex: 'none',
                  order: 5,
                  flexGrow: 0
                }}
              >
                {/* Close Button */}
                <button
                  onClick={() => {
                    // Discard refine popup if visible
                    if (refinePopover.isVisible) {
                      setRefinePopover({
                        isVisible: false,
                        originalRules: [],
                        refinedRules: [],
                      });
                    }

                    // Check if document is incomplete (empty name)
                    const isIncomplete = !document.name.trim();

                    if (isIncomplete) {
                      // Remove incomplete document entirely
                      setDocumentsState(prev => ({
                        ...prev,
                        documents: prev.documents.filter(doc => doc.id !== document.id),
                        // Don't adjust totalFilesUsed since incomplete documents aren't counted yet
                      }));
                    } else {
                      // Just close edit mode for complete documents
                      setDocumentsState(prev => ({
                        ...prev,
                        documents: prev.documents.map(doc =>
                          doc.id === document.id ? { ...doc, isEditing: false } : doc
                        )
                      }));
                    }
                  }}
                  title="Close"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '4px',
                    gap: '6px',
                    width: '24px',
                    height: '24px',
                    background: '#F4F4F5',
                    borderRadius: '6px',
                    flex: 'none',
                    order: 0,
                    flexGrow: 0,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {/* X Icon */}
                  <X size={16} strokeWidth={1.5} color="#71717A" />
                </button>

                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={!isDocumentValid() || isSaving || isRefining || refinePopover.isVisible}
                  title="Save"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '4px',
                    gap: '6px',
                    width: '24px',
                    height: '24px',
                    background: (!isDocumentValid() || isSaving || isRefining || refinePopover.isVisible) ? '#A1A1AA' : '#006FEE',
                    borderRadius: '6px',
                    flex: 'none',
                    order: 1,
                    flexGrow: 0,
                    border: 'none',
                    cursor: (!isDocumentValid() || isSaving || isRefining || refinePopover.isVisible) ? 'not-allowed' : 'pointer',
                    opacity: (!isDocumentValid() || isSaving || isRefining || refinePopover.isVisible) ? 0.6 : 1
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L7 12L13 4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Frame 3198 - Optional Toggle */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '8px',
                width: '90px',
                height: '20px',
                borderRadius: '0px',
                flex: 'none',
                order: 1,
                flexGrow: 0
              }}
            >
              <span
                style={{
                  width: '48px',
                  height: '20px',
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#3F3F46',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0
                }}
              >
                Optional
              </span>
              <Switch
                isSelected={formData.isOptional}
                onValueChange={(value) => setFormData(prev => ({ ...prev, isOptional: value }))}
                disabled={isCreating}
                size="sm"
                aria-label="Toggle document optional status"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: '0px',
                  width: '34px',
                  height: '20px',
                  flex: 'none',
                  order: 1,
                  flexGrow: 0
                }}
              />
            </div>

            {/* Frame 6305 - Number of Files Section */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '12px',
                width: '424px',
                height: '44px',
                flex: 'none',
                order: 2,
                alignSelf: 'stretch',
                flexGrow: 0
              }}
            >
              {/* Frame 6303 - Number of Files Header */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '8px',
                  width: '424px',
                  height: '20px',
                  flex: 'none',
                  order: 0,
                  alignSelf: 'stretch',
                  flexGrow: 0
                }}
              >
                <span
                  style={{
                    width: '383px',
                    height: '20px',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: '#3F3F46',
                    flex: 'none',
                    order: 0,
                    flexGrow: 1
                  }}
                >
                  Number of Files
                </span>
                <span
                  style={{
                    width: '33px',
                    height: '20px',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: '#3F3F46',
                    flex: 'none',
                    order: 1,
                    flexGrow: 0
                  }}
                >
                  {isNaN(formData.fileCount) ? 1 : formData.fileCount}/{maxSliderValue}
                </span>
              </div>

              {/* Frame 6301 - Slider */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: '0px',
                  isolation: 'isolate',
                  width: '424px',
                  height: '12px',
                  borderRadius: '8px',
                  flex: 'none',
                  order: 1,
                  alignSelf: 'stretch',
                  flexGrow: 0
                }}
              >
                <Slider
                  key={`${document.id}-${maxSliderValue}`}
                  value={formData.fileCount}
                  onChange={(value) => {
                    const numValue = Array.isArray(value) ? value[0] : value;
                    const clampedValue = Math.max(1, Math.min(maxSliderValue, Number(numValue) || 1));
                    setFormData(prev => ({ ...prev, fileCount: clampedValue }));
                  }}
                  minValue={1}
                  maxValue={maxSliderValue}
                  step={1}
                  isDisabled={isCreating}
                  size="sm"
                  aria-label="Number of files"
                  classNames={{
                    track: "bg-[#F4F4F5] h-3 rounded-lg",
                    filler: "bg-[#006FEE] h-3",
                    thumb: "w-5 h-5 bg-white border-[1.5px] border-[#006FEE] shadow-md"
                  }}
                  style={{
                    width: '100%',
                    height: '12px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Validation Rules */}
          <div className="space-y-2">
            <ValidationRulesComponent
              defaultValue={formData.validationRules}
              onGetValues={() => validationRulesRef.current?.getCurrentValues() || []}
              onSourceClick={() => { }} // no-op
              setListOfSourcesForSelectedSources={() => { }} // no-op
              placeholder="Enter validation rules..."
              isDisabled={isCreating}
              onRefineClick={handleRefineValidationRules}
              isRefining={isRefining}
              onEdit={handleDiscardRefinedRules}
              validationRulesRef={validationRulesRef}
            />
          </div>
        </div>

        {/* Refine Popup Portal */}
        {refinePopover.isVisible && cardRef.current && (() => {
          const rect = cardRef.current!.getBoundingClientRect();
          return createPortal(
            <>
              {/* Refine Popup */}
              <div
                style={{
                  position: 'absolute',
                  top: rect.bottom + window.scrollY + 16,
                  left: rect.left + window.scrollX,
                  zIndex: 9999,
                  width: rect.width,
                  minHeight: 136,
                  maxHeight: 'calc(100vh - 100px)',
                  padding: 12,
                  gap: 12,
                  backgroundImage: `
                  linear-gradient(#fff, #fff),
                  linear-gradient(180deg, #FF6496 0%, #812DD6 100%)
                `,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.15)',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  border: '1px solid transparent',
                  borderRadius: 16,
                }}
              >
                {/* Header Row */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    padding: 0,
                    gap: 24,
                    width: '100%',
                    height: 20,
                    marginBottom: 12,
                  }}
                >
                  {/* Title */}
                  <div
                    style={{
                      height: 20,
                      fontFamily: 'Inter',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#18181B',
                      flex: 'none',
                      order: 0,
                      flexGrow: 0,
                    }}
                  >
                    Accept refined version?
                  </div>
                  {/* Buttons */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 0,
                      gap: 12,
                      width: 133,
                      height: 20,
                      flex: 'none',
                      order: 1,
                      flexGrow: 0,
                    }}
                  >
                    {/* Accept Button */}
                    <button
                      onClick={handleAcceptRefinedRules}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 0,
                        gap: 6,
                        width: 59,
                        height: 20,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: 12,
                        color: '#18181B',
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6.5L5.5 9L9 4"
                            stroke="#18181B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span
                        style={{
                          width: 41,
                          height: 20,
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontSize: 12,
                          lineHeight: '20px',
                          color: '#18181B',
                          marginLeft: 2,
                        }}
                      >
                        Accept
                      </span>
                    </button>
                    {/* Discard Button */}
                    <button
                      onClick={handleDiscardRefinedRules}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 0,
                        gap: 6,
                        width: 62,
                        height: 20,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: 12,
                        color: '#18181B',
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 3L3 9"
                            stroke="#18181B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3 3L9 9"
                            stroke="#18181B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span
                        style={{
                          width: 44,
                          height: 20,
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontSize: 12,
                          lineHeight: '20px',
                          color: '#18181B',
                          marginLeft: 2,
                        }}
                      >
                        Discard
                      </span>
                    </button>
                  </div>
                </div>
                {/* Content - Display refined rules as numbered list */}
                <div
                  style={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: 14,
                    lineHeight: '20px',
                    color: '#3F3F46',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: 'calc(100vh - 300px)',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    padding: '8px 0',
                  }}
                >
                  {refinePopover.refinedRules.map((rule, index) => (
                    <div key={index} style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                      <span style={{ flexShrink: 0 }}>{index + 1}.</span>
                      <span style={{ wordBreak: 'break-word', flex: 1 }}>{rule}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>,
            window.document.body
          );
        })()}
      </>
    );
  }

  // Don't render incomplete documents in display mode
  const isIncomplete = !document.name.trim();
  if (isIncomplete && !document.isEditing) {
    return null;
  }

  // Display mode
  return (
    <div
      className="border border-[#E4E4E7] rounded-lg p-4 cursor-pointer hover:border-[#006FEE] transition-colors group min-h-[120px] flex flex-col gap-2"
      onClick={onEdit}
    >
      {/* Header Section */}
      <div className="flex flex-col gap-2">
        {/* Title and Buttons Row */}
        <div className="flex flex-row items-center justify-between">
          {/* Document Title */}
          <h4 className="text-sm font-medium text-[#18181B] truncate flex-1 pr-2">
            {document.name}
          </h4>

          {/* Buttons Container */}
          <div className="flex flex-row gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {/* Edit Button */}
            <button
              className="flex justify-center items-center w-6 h-6 bg-[#F4F4F5] rounded-md"
              title="Edit document"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 2L14 4L5 13L1 14L2 10L11 1" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Delete Button */}
            <button
              className="flex justify-center items-center w-6 h-6 bg-[#F4F4F5] rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete document"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isDeletingDocument}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h8.667z" stroke="#717171" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Response Fields Section */}
      <div className="flex flex-col gap-2 flex-1">
        {/* Status and File Count Row */}
        <div className="flex flex-row items-center gap-2 text-xs text-[#71717A]">
          <span>{document.isOptional ? 'Optional' : 'Mandatory'}</span>
          <span>•</span>
          <span>{document.fileCount} file{document.fileCount !== 1 ? 's' : ''}</span>
        </div>

        {/* Validation Rules */}
        <div className="text-xs text-[#71717A] space-y-1">
          {document.validationRules.value.slice(0, 2).map((rule, index) => (
            <div key={index} className="truncate">
              {index + 1}. {rule.trim()}
            </div>
          ))}
          {document.validationRules.value.length > 2 && (
            <div className="italic">
              +{document.validationRules.value.length - 2} more rule{document.validationRules.value.length - 2 !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface DocumentRule {
  id: string;
  text: string;
}

interface Document {
  id: string;
  name: string;
  isOptional: boolean;
  fileCount: number;
  validationRules: {
    value: string[];
    sources: ReviewSourceTypes[];
    isEdited?: boolean;
  };
  isEditing?: boolean;
}

interface DocumentsState {
  documents: Document[];
  totalFilesUsed: number;
}

interface CreateQuestionnaireScreenProps {
  onClose?: () => void;
  questionnaireId?: string;
}

const CreateQuestionnaireScreen = ({ onClose, questionnaireId }: CreateQuestionnaireScreenProps) => {
  const navigate = useNavigate();
  const params = useParams<{ questionnaireId: string }>();

  // Use questionnaireId from props (wizard mode) or URL params (edit mode)
  const currentQuestionnaireId = questionnaireId || params.questionnaireId;
  const isEditMode = !!currentQuestionnaireId;
  const [questionnaireName, setQuestionnaireName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('questions');
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState<Questionnaire | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(new Set());
  const [duplicateOptionsByQuestion, setDuplicateOptionsByQuestion] = useState<Map<string, Set<number>>>(new Map());
  const [isPublished, setIsPublished] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const questionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [isExtractingQuestions, setIsExtractingQuestions] = useState(false);

  // Socket setup for questions extraction
  const { addListener, removeListener } = useSocket("main", () => getZaniaSocket());

  const methods = useForm({
    resolver: yupResolver(questionnaireFormSchema),
    defaultValues: {
      questionnaireId: questionnaireId || "",
      questions: [],
    },
  });

  // Documents state
  const [documentsState, setDocumentsState] = useState<DocumentsState>({
    documents: [],
    totalFilesUsed: 0,
  });

  // Track if documents have been modified
  const [initialDocumentsState, setInitialDocumentsState] = useState<DocumentsState>({
    documents: [],
    totalFilesUsed: 0,
  });

  // Use React Hook Form's formState.isDirty for change detection
  const { isDirty, dirtyFields } = methods.formState;

  // Check if there are unsaved changes
  useEffect(() => {
    // Check if documents changed
    const documentsChanged = JSON.stringify(documentsState) !== JSON.stringify(initialDocumentsState);

    // Update hasUnsavedChanges flag based on form dirty state or documents changed
    setHasUnsavedChanges(isDirty || documentsChanged);
  }, [isDirty, documentsState, initialDocumentsState, dirtyFields]);

  // Prevent accidental browser closure when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only prevent navigation if there are unsaved changes
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Block navigation within the app when there are unsaved changes
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if we're staying on the same route
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    // Block navigation only if there are unsaved changes
    return hasUnsavedChanges;
  });

  // Handle blocked navigation by showing ExitModal
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsExitModalOpen(true);
    }
  }, [blocker.state]);

  // Transform Evidence to Document format
  const transformEvidenceToDocument = (evidence: Evidence): Document => {
    return {
      id: evidence.id,
      name: evidence.document_name,
      isOptional: !evidence.required,
      fileCount: evidence.max_number_of_files,
      validationRules: {
        value: evidence.rules,
        sources: [],
        isEdited: false
      }
    };
  };

  const fetchQuestionsFromQuestionnaireUrl = async (questionnaireData: QuestionnaireDetailsResponse) => {
    if (!questionnaireData?.questionnaire.questionnaire_json_url) return;

    // setFetchingControls(true);
    try {
      // Use the same URL refresh logic as other parts of the app
      const validSignedUrl = await checkAndRefreshUrl(questionnaireData.questionnaire.questionnaire_json_url);

      // Fetch controls data directly from blob storage
      const response = await fetch(validSignedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch questions: ${response.statusText}`);
      }

      const questionsListData = await response.json();

      return questionsListData;
    } catch (error) {
      console.error('Error fetching controls:', error);
      addNotification({
        type: "error",
        title: "Failed to load Questionnaire",
        message: "Could not fetch control set data. Please try again.",
      });
    } finally {
      // setFetchingControls(false);
    }
  };

  // Load questionnaire data when in edit mode
  useEffect(() => {
    const loadQuestionnaire = async () => {
      if (!isEditMode || !currentQuestionnaireId) return;

      setIsLoading(true);
      try {

        const resp = await (getQuestionnaireDetails(currentQuestionnaireId));
        const questionnaireData = resp?.questionnaire;

        if (questionnaireData) {
          setQuestionnaireName(questionnaireData.questionnaire_name);
          setIsPublished(questionnaireData.published || false);

          if (questionnaireData && questionnaireData.number_of_questions > 0) {
            // load questions first and then open questionnaire modal
            const questionsList = await fetchQuestionsFromQuestionnaireUrl(resp);
            if (questionsList.length) {
              // Reconstruct parent-child relationships from question_number
              const questionsWithMapping = questionsList.map((question: any) => {
                const questionNumber = question.question_number;

                // If question_number has a dot (e.g., "2.a", "2.a.b"), it's a sub-question
                if (questionNumber && questionNumber.includes('.')) {
                  const parts = questionNumber.split('.');

                  // Get parent question number (everything except the last part)
                  const parentNumber = parts.slice(0, -1).join('.');
                  const lastPart = parts[parts.length - 1]; // e.g., "a", "b"

                  // Find parent question by its question_number
                  const parentQuestion = questionsList.find((q: any) => q.question_number === parentNumber);

                  if (parentQuestion) {
                    // Check if parent has options (response_choices)
                    if (parentQuestion.response_choices && parentQuestion.response_choices.length > 0) {
                      // Calculate which option this maps to (a=0, b=1, c=2, ...)
                      const optionIndex = lastPart.charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.

                      return {
                        ...question,
                        parent_question_id: parentQuestion.id,
                        parent_question_response_option_index: optionIndex,
                      };
                    } else {
                      // Parent is a text field (no options), just set parent_question_id
                      return {
                        ...question,
                        parent_question_id: parentQuestion.id,
                        parent_question_response_option_index: null,
                      };
                    }
                  }
                }

                // Root question or couldn't find parent
                return question;
              });

              methods.reset({
                questions: questionsWithMapping,
                questionnaireId: questionnaireData.id
              });

            }
          } else {
            methods.reset({
              questions: [],
              questionnaireId: questionnaireData.id
            });
          }

          // Reset hasUnsavedChanges after loading
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error('Error loading questionnaire:', error);
        addNotification({
          type: 'error',
          message: 'An error occurred while loading questionnaire data',
        });
        navigate(`/${ROUTES.SETTINGS}/questionnaires`);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestionnaire();
  }, [isEditMode, currentQuestionnaireId, navigate]);

  // Load evidences for documents tab
  useEffect(() => {
    const loadEvidences = async () => {
      // Only load evidences if we have a questionnaireId (edit mode)
      if (!currentQuestionnaireId) {
        // For create mode, start with empty documents state
        setDocumentsState({
          documents: [],
          totalFilesUsed: 0
        });
        return;
      }

      try {
        const [error, response] = await parsePromise(getQuestionnaireEvidences(currentQuestionnaireId));

        if (error) {
          console.error('Failed to load evidences:', error);
          addNotification({
            type: 'error',
            message: 'Failed to load documents. Please try again.',
          });
          return;
        }

        if (response?.success && response.evidences) {
          const documents = response.evidences.map(transformEvidenceToDocument);
          const totalFiles = documents.reduce((sum, doc) => sum + doc.fileCount, 0);

          const newDocumentsState = {
            documents,
            totalFilesUsed: totalFiles
          };

          setDocumentsState(newDocumentsState);
          // Store initial state for comparison
          setInitialDocumentsState(JSON.parse(JSON.stringify(newDocumentsState)));
        }
      } catch (error) {
        console.error('Error loading evidences:', error);
        addNotification({
          type: 'error',
          message: 'Failed to load documents. Please try again.',
        });
      }
    };

    loadEvidences();
  }, [currentQuestionnaireId]);

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
  }, []);

  const handleClose = useCallback(() => {
    // If there are unsaved changes, show ExitModal
    if (hasUnsavedChanges) {
      setIsExitModalOpen(true);
      return;
    }

    // No unsaved changes, just close
    if (onClose) {
      onClose();
    } else {
      navigate(`/${ROUTES.SETTINGS}/questionnaires`);
    }
  }, [hasUnsavedChanges, onClose, navigate]);

  const handleExitWithoutSaving = useCallback(() => {
    setIsExitModalOpen(false);

    // Reset unsaved changes to allow navigation
    setHasUnsavedChanges(false);

    // If navigation was blocked, proceed with it
    if (blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }

    if (onClose) {
      onClose();
    } else {
      // Navigate back to questionnaires list
      navigate(`/${ROUTES.SETTINGS}/questionnaires`);
    }
  }, [onClose, navigate, blocker]);

  const scrollToInvalidQuestion = useCallback((questionId: string) => {
    const element = questionRefs.current.get(questionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleSave = async () => {
    if (!questionnaireName.trim()) {
      setErrorMessage("Please enter a questionnaire name");
      return;
    }

    // Check if we're in edit mode (have currentQuestionnaireId)
    if (!currentQuestionnaireId) {
      // Create mode - just create the questionnaire
      setIsCreating(true);
      try {
        const [error, response] = await parsePromise(createQuestionnaire(questionnaireName.trim()));

        if (error) {
          setErrorMessage(error?.message || "Name already exists. Please choose a different name to proceed.");
          return;
        }

        if (response?.success) {
          addNotification({
            type: "success",
            message: `"${response.questionnaire.questionnaire_name}" created successfully.`,
          });

          if (onClose) {
            onClose();
          } else {
            // Navigate back to questionnaires list
            navigate(`/${ROUTES.SETTINGS}/questionnaires`);
          }
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        console.error("Create questionnaire error:", error);
        setErrorMessage("An unexpected error occurred. Please try again later.");
      } finally {
        setIsCreating(false);
      }
      return;
    }

    // Edit mode - check if there are any changes first
    if (!hasUnsavedChanges) {
      // No changes, just close without calling API
      if (onClose) {
        onClose();
      } else {
        navigate(`/${ROUTES.SETTINGS}/questionnaires`);
      }
      return;
    }

    // Edit mode - validate and save questions
    // Trigger form validation
    const isValid = await methods.trigger();

    if (!isValid) {
      // Get validation errors
      const errors = methods.formState.errors;
      const invalidIds = new Set<string>();
      const duplicateOptionsMap = new Map<string, Set<number>>();
      const questions = methods.getValues("questions") as any[] || [];

      // Collect all invalid question IDs based on form errors
      if (errors.questions && Array.isArray(errors.questions)) {
        errors.questions.forEach((questionError: any, index: number) => {
          if (questionError && questions[index]) {
            invalidIds.add(questions[index].id);

            // Check if this question has duplicate option errors
            if (questionError.response_choices && questions[index].response_choices) {
              const duplicateIndices = findDuplicateOptionIndices(questions[index].response_choices);
              if (duplicateIndices.size > 0) {
                duplicateOptionsMap.set(questions[index].id, duplicateIndices);
              }
            }
          }
        });
      }

      if (invalidIds.size > 0) {
        // Find the first invalid question and scroll to it
        const firstInvalidQuestion = questions.find((q) => invalidIds.has(q.id));

        if (firstInvalidQuestion) {
          setInvalidQuestionIds(invalidIds);
          setDuplicateOptionsByQuestion(duplicateOptionsMap);
          scrollToInvalidQuestion(firstInvalidQuestion.id);

          // Check if we have duplicate options error
          const hasDuplicateOptions = duplicateOptionsMap.size > 0;

          addNotification({
            type: "error",
            message: hasDuplicateOptions
              ? "Please fix duplicate options in questions before saving."
              : "Please complete all questions. Questions must have question text and options (for choice-based questions).",
          });
        }
      }
      return;
    }

    // Clear any previous invalid highlights
    setInvalidQuestionIds(new Set());
    setDuplicateOptionsByQuestion(new Map());

    // Save all questions using new Save Questions API
    setIsCreating(true);
    try {
      const questions = methods.getValues("questions") as any[] || [];

      // Helper function to build question numbers
      const buildQuestionNumber = (question: any, index: number): string => {
        if (question.parent_question_id) {
          // Find parent question
          const parentIdx = questions.findIndex(q => q.id === question.parent_question_id);
          if (parentIdx !== -1) {
            const parentQuestion = questions[parentIdx];
            const parentNumber = buildQuestionNumber(parentQuestion, parentIdx);

            // Check if parent has options (single_choice or multiple_choice)
            if (parentQuestion.response_choices && parentQuestion.response_choices.length > 0) {
              // Use the stored option index directly (not the option text)
              const optionIdx = question.parent_question_response_option_index;

              if (optionIdx !== null && optionIdx !== undefined) {
                return `${parentNumber}.${String.fromCharCode(97 + optionIdx)}`;
              }
            } else {
              // Parent is a text field (no options)
              // Only one question can be mapped to a text field, so always use ".a"
              return `${parentNumber}.a`;
            }
          }
        }

        // For root questions, calculate index among non-sub-questions
        const rootQuestions = questions.filter(q => !q.parent_question_id);
        const rootIndex = rootQuestions.findIndex(q => q.id === question.id);
        return String(rootIndex + 1);
      };

      // Build payload for all questions
      const questionsPayload: SaveQuestionPayload[] = questions.map((question, index) => {
        const questionNumber = buildQuestionNumber(question, index);

        // Find parent question to get its type
        const parentQuestion = question.parent_question_id
          ? questions.find(q => q.id === question.parent_question_id)
          : null;

        // Determine response_choices based on question type
        // For text field, response_choices should be empty
        // For single_choice, multiple_choice, and dropdown, keep the choices
        const responseChoices = question.question_type === 'free_text'
          ? []
          : deduplicateResponseChoices(question.response_choices);

        // Trim whitespace from question text
        const trimmedQuestion = question.question?.trim() || '';

        return {
          question_number: questionNumber,
          question: trimmedQuestion,
          question_type: question.question_type,
          is_dropdown: question.is_dropdown || false,
          response_choices: responseChoices,
          required: question.required ?? true,
          comments_allowed: question.comments_allowed ?? false,
          parent_question_type: parentQuestion ? parentQuestion.question_type : undefined,
          // parent_question_response_option removed - backend doesn't need it
        };
      });

      // Call new Save Questions API
      const [error, response] = await parsePromise(
        saveQuestions(currentQuestionnaireId, { questions: questionsPayload })
      );

      if (error) {
        console.error('Failed to save questions:', error);
        addNotification({
          type: "error",
          message: error?.response?.data?.error_message || "Failed to save questions. Please try again.",
        });
        setIsCreating(false);
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: `"${questionnaireName}" ${isEditMode ? 'Updated' : 'Saved'} successfully.`,
        });

        // Update form state with response from API (which includes real IDs)
        if (response.questions && response.questions.length > 0) {
          // Reset form with new data to mark as clean
          methods.reset({
            questions: response.questions,
            questionnaireId: currentQuestionnaireId
          });
        }

        // Reset unsaved changes flag after successful save
        setHasUnsavedChanges(false);

        if (onClose) {
          onClose();
        } else {
          // Navigate back to questionnaires list
          navigate(`/${ROUTES.SETTINGS}/questionnaires`);
        }
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Save questions error:", error);
      addNotification({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveAndExit = async () => {
    // Call the save handler
    await handleSave();

    // If there were validation errors, handleSave would have returned early
    // and isCreating would still be false. In that case, close the modal.
    // If save was successful, hasUnsavedChanges would be false and the component
    // would have navigated away.
    // So we only need to close the modal if we're still here with errors.
    setIsExitModalOpen(false);
  };

  // Socket listener for questions extraction
  useEffect(() => {
    if (!currentQuestionnaireId) return;

    const handleQuestionsExtracted = async (data: {
      status?: boolean;
      message?: string;
      questionnaire_json_url?: string;
      questionnaire_id: string;
      data?: null;
      error?: null;
    }) => {
      if (data.questionnaire_id !== currentQuestionnaireId) return;

      // Handle error response from backend
      if (data.status === false || !data.questionnaire_json_url) {
        setIsExtractingQuestions(false);
        addNotification({
          type: "error",
          title: "Question Extraction Failed",
          message: "Failed to extract questions from the document. Please try again.",
        });
        // Keep the uploaded files so user can try with a different document
        // The user can manually remove the failed file and upload a new one

        // Clear the failed file so user can immediately upload a new document
        setUploadedFiles([]);
        return;
      }

      try {
        setIsExtractingQuestions(true);

        // Fetch questions from the JSON URL
        const validSignedUrl = await checkAndRefreshUrl(data.questionnaire_json_url);
        const response = await fetch(validSignedUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch questions: ${response.statusText}`);
        }

        const questionsList = await response.json();

        if (questionsList && questionsList.length > 0) {
          // Reconstruct parent-child relationships from question_number
          const questionsWithMapping = questionsList.map((question: any) => {
            const questionNumber = question.question_number;

            // If question_number has a dot (e.g., "2.a", "2.a.b"), it's a sub-question
            if (questionNumber && questionNumber.includes('.')) {
              const parts = questionNumber.split('.');

              // Get parent question number (everything except the last part)
              const parentNumber = parts.slice(0, -1).join('.');
              const lastPart = parts[parts.length - 1]; // e.g., "a", "b"

              // Find parent question by its question_number
              const parentQuestion = questionsList.find((q: any) => q.question_number === parentNumber);

              if (parentQuestion) {
                // Check if parent has options (response_choices)
                if (parentQuestion.response_choices && parentQuestion.response_choices.length > 0) {
                  // Calculate which option this maps to (a=0, b=1, c=2, ...)
                  const optionIndex = lastPart.charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.

                  return {
                    ...question,
                    parent_question_id: parentQuestion.id,
                    parent_question_response_option_index: optionIndex,
                  };
                } else {
                  // Parent is a text field (no options), just set parent_question_id
                  return {
                    ...question,
                    parent_question_id: parentQuestion.id,
                    parent_question_response_option_index: null,
                  };
                }
              }
            }

            // Root question or couldn't find parent
            return question;
          });

          methods.reset({
            questions: questionsWithMapping,
            questionnaireId: currentQuestionnaireId
          });

          addNotification({
            type: "success",
            message: `Successfully imported ${questionsWithMapping.length} questions`,
          });

          // Clear uploaded files after successful import
          setUploadedFiles([]);
        }
      } catch (error) {
        console.error('Error fetching extracted questions:', error);
        addNotification({
          type: "error",
          title: "Failed to load questions",
          message: "Could not fetch extracted questions. Please try again.",
        });
      } finally {
        setIsExtractingQuestions(false);
      }
    };

    addListener<{
      status?: boolean;
      message?: string;
      questionnaire_json_url?: string;
      questionnaire_id: string;
      data?: null;
      error?: null;
    }>("questions:extracted:from:document", handleQuestionsExtracted);

    return () => {
      removeListener<{
        status?: boolean;
        message?: string;
        questionnaire_json_url?: string;
        questionnaire_id: string;
        data?: null;
        error?: null;
      }>("questions:extracted:from:document", handleQuestionsExtracted);
    };
  }, [currentQuestionnaireId, addListener, removeListener, methods]);

  const handleFileChange = async (files: UploadedFile[]) => {
    console.log("files is ", files)
    setUploadedFiles(files);

    // Only proceed if there's exactly 1 file with no errors
    // Check total file count first (including files with errors)
    if (files.length !== 1) {
      // Multiple files or no files - don't extract
      return;
    }

    // Check if the single file is successfully uploaded
    const successfulFiles = files.filter(f => f.status === "success" && f.signedUrl);

    if (successfulFiles.length === 1 && currentQuestionnaireId) {
      try {
        setIsExtractingQuestions(true);

        // Use the successfully uploaded file
        const file = successfulFiles[0];

        const [error, response] = await parsePromise(
          extractQuestionsFromDocument(currentQuestionnaireId, {
            document_url: file.signedUrl!
          })
        );

        if (error) {
          console.error('Failed to extract questions:', error);
          addNotification({
            type: "error",
            message: error?.message || "Failed to extract questions from document. Please try again.",
          });
          setIsExtractingQuestions(false);
          return;
        }

        if (response?.success) {
          addNotification({
            type: "info",
            message: "Questions extraction started. This may take a few moments...",
          });
          // Keep isExtractingQuestions true - will be set to false by socket event
        }
      } catch (error) {
        console.error('Error extracting questions:', error);
        addNotification({
          type: "error",
          message: "An error occurred while extracting questions",
        });
        setIsExtractingQuestions(false);
      }
    }
  };

  const handleRequestDocuments = () => {
    addNotification({
      type: "info",
      message: "Request Documents functionality will be available in a future release.",
    });
  };

  const isSaveDisabled = !questionnaireName.trim() || isCreating || (methods.watch("questions")?.length === 0 && documentsState.documents.filter(documentItem => !documentItem.isEditing).length === 0) || isLoading;

  return (
    <>
      <div className={`w-full h-full bg-[#FEFEFE] rounded-lg border border-[#E4E4E7] shadow-lg flex flex-col overflow-hidden relative ${isCreating ? 'cursor-wait' : ''}`}>
        {/* Loading overlay to prevent interactions during save */}
        {isCreating && (
          <div
            className="absolute inset-0 z-50 cursor-wait"
            style={{ pointerEvents: 'all' }}
          />
        )}

        {/* Header Section */}
        <div className="flex flex-col items-start p-4 gap-4 w-full bg-[#FEFEFE] border-b border-[#E4E4E7] flex-shrink-0">
          <div className="flex flex-row items-center justify-between w-full">
            {/* Left Section - Close button and questionnaire name input */}
            <div className="flex flex-row items-center gap-3 flex-1">
              <button
                className="text-[#18181B] hover:text-gray-600"
                onClick={handleClose}
                disabled={isCreating}
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <div className="min-w-[300px]">
                <input
                  type="text"
                  placeholder={isEditMode ? "Questionnaire Name" : "Add Questionnaire Name"}
                  value={questionnaireName}
                  onChange={(e) => {
                    setQuestionnaireName(e.target.value);
                    setErrorMessage("");
                  }}
                  className="text-sm font-medium placeholder:text-[#A1A1AA] bg-transparent border-none outline-none w-full"
                  disabled={isCreating}
                  readOnly={isEditMode}
                  maxLength={50}
                />
              </div>
            </div>

            {/* Middle Section - Tabs */}
            <div className="flex flex-row justify-center items-center flex-1">
              <div className="flex flex-row items-center p-0.5 gap-0.5 w-[194px] h-8 bg-[#F4F4F5] rounded-lg">
                {/* Questions Tab - First */}
                <button
                  type="button"
                  onClick={() => handleTabChange('questions')}
                  className={cn(
                    "flex flex-col justify-center items-center px-4 py-1 gap-1 w-[90px] h-7 rounded-md transition-all duration-200",
                    activeTab === 'questions'
                      ? "bg-white shadow-sm"
                      : "bg-transparent"
                  )}
                  style={{
                    flex: 'none',
                    order: 0,
                    flexGrow: 0,
                    boxShadow: activeTab === 'questions' ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  disabled={isCreating}
                >
                  <span
                    className={cn(
                      "w-[58px] h-5 text-xs leading-5",
                      activeTab === 'questions' ? "text-[#18181B] font-medium" : "text-[#3F3F46] font-normal"
                    )}
                    style={{
                      fontFamily: 'Inter',
                      fontStyle: 'normal',
                      fontWeight: activeTab === 'questions' ? 500 : 400,
                      fontSize: '12px',
                      lineHeight: '20px',
                      flex: 'none',
                      order: 0,
                      flexGrow: 0
                    }}
                  >
                    Questions
                  </span>
                </button>

                {/* Documents Tab - Second */}
                <button
                  type="button"
                  onClick={() => handleTabChange('documents')}
                  className={cn(
                    "flex flex-col justify-center items-center px-4 py-1 gap-1 w-[98px] h-7 rounded-md relative transition-all duration-200",
                    activeTab === 'documents'
                      ? "bg-white shadow-sm"
                      : "bg-transparent"
                  )}
                  style={{
                    isolation: 'isolate',
                    flex: 'none',
                    order: 1,
                    flexGrow: 0,
                    boxShadow: activeTab === 'documents' ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  disabled={isCreating}
                >
                  <span
                    className={cn(
                      "w-[66px] h-5 text-xs leading-5",
                      activeTab === 'documents' ? "text-[#18181B] font-medium" : "text-[#3F3F46] font-normal"
                    )}
                    style={{
                      fontFamily: 'Inter',
                      fontStyle: 'normal',
                      fontWeight: activeTab === 'documents' ? 500 : 400,
                      fontSize: '12px',
                      lineHeight: '20px',
                      flex: 'none',
                      order: 0,
                      flexGrow: 0,
                      zIndex: 0
                    }}
                  >
                    Documents
                  </span>
                </button>
              </div>
            </div>

            {/* Right Section - Save button */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              <ZButton
                color="primary"
                onClick={handleSave}
                isDisabled={isSaveDisabled}
                isLoading={isCreating}
                className="text-xs font-medium h-7 px-3"
              >
                Done
              </ZButton>
            </div>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="text-xs text-red-500 ml-8">
              {errorMessage}
            </div>
          )}
        </div>


        {/* Main Content Area */}
        <div className="flex-1 min-h-0 p-4 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner
                containerClassName="bg-transparent relative"
                className="mx-auto"
              />
            </div>
          ) : (
            <>
              {/* Questions Tab - Keep mounted but hide when inactive */}
              <div className={clsx("h-full", activeTab === 'questions' ? 'block' : 'hidden')}>
                {/* Questions List Form */}
                <FormProvider {...methods}>
                  <form className={clsx("overflow-hidden", {
                    "size-full": (methods.watch("questions")?.length ?? 0) > 0,
                  })}>
                    <QuestionList
                      invalidQuestionIds={invalidQuestionIds}
                      duplicateOptionsByQuestion={duplicateOptionsByQuestion}
                      onQuestionEdit={(questionId) => {
                        // Remove from invalid list when user starts editing
                        setInvalidQuestionIds(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(questionId);
                          return newSet;
                        });
                        // Recalculate duplicate options for this question
                        const questions = methods.getValues("questions") as any[] || [];
                        const question = questions.find((q: any) => q.id === questionId);

                        if (question && question.response_choices) {
                          const duplicateIndices = findDuplicateOptionIndices(question.response_choices);

                          setDuplicateOptionsByQuestion(prev => {
                            const newMap = new Map(prev);
                            if (duplicateIndices.size > 0) {
                              // Update with new duplicate indices
                              newMap.set(questionId, duplicateIndices);
                            } else {
                              // No duplicates, remove from map
                              newMap.delete(questionId);
                            }
                            return newMap;
                          });
                        } else {
                          // No options or question not found, clear duplicates
                          setDuplicateOptionsByQuestion(prev => {
                            const newMap = new Map(prev);
                            newMap.delete(questionId);
                            return newMap;
                          });
                        }
                      }}
                      questionRefs={questionRefs}
                      isExtractingQuestions={isExtractingQuestions}
                    />
                  </form>
                </FormProvider>
                {methods.watch("questions")?.length === 0 && (
                  <div className="flex justify-center">
                    <div className="w-full max-w-2xl">
                      <div className="bg-white p-6 rounded-lg">
                        <h4 className="text-sm font-medium text-[#11181C] mb-4">
                          Import Questions from File
                        </h4>
                        {!currentQuestionnaireId ? (
                          <div className="text-sm text-[#71717A] p-4 bg-[#F4F4F5] rounded-lg">
                            Please create a questionnaire first by clicking "Save" before importing questions.
                          </div>
                        ) : isExtractingQuestions ? (
                          <div className="flex flex-col items-center justify-center gap-3 p-8">
                            <LoadingSpinner
                              containerClassName="bg-transparent relative"
                              className="mx-auto"
                            />
                            <p className="text-sm text-[#71717A]">
                              Extracting questions from document...
                            </p>
                          </div>
                        ) : (
                          <FileUpload
                            isMultiple={false}
                            maxFiles={1}
                            allowedFileTypes={[
                              "application/vnd.ms-excel",
                              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                              "application/pdf",
                              "application/msword",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            ]}
                            setFiles={handleFileChange}
                            maxSizeMB={20}
                            orientation="vertical"
                            initialFiles={uploadedFiles}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Documents Tab - Keep mounted but hide when inactive */}
              <div className={activeTab === 'documents' ? 'block' : 'hidden'}>
                <DocumentsTab
                  documentsState={documentsState}
                  setDocumentsState={setDocumentsState}
                  isCreating={isCreating}
                  currentQuestionnaireId={currentQuestionnaireId}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Exit Modal - shown when there are unsaved changes */}
      <ExitModal
        isOpen={isExitModalOpen}
        onClose={() => {
          setIsExitModalOpen(false);
          // If navigation was blocked, reset the blocker
          if (blocker.state === 'blocked') {
            blocker.reset();
          }
        }}
        onExitWithoutSaving={handleExitWithoutSaving}
        onSaveAndExit={handleSaveAndExit}
        title="Exit without saving changes?"
        description="Any unsaved changes to this questionnaire will be lost."
        isSaving={isCreating}
      />
    </>
  );
};

export default CreateQuestionnaireScreen;